import { Alert } from '@creditornot/cb-alert';
import { Button } from '@creditornot/cb-button';
import { InputContainer } from '@creditornot/cb-form';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Input } from '@creditornot/cb-input';
import { Select } from '@creditornot/cb-select';
import { getCookieName } from '@creditornot/cwc-cookie-sdk';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { useQuery } from '@tanstack/react-query';
import { FORM_ERROR } from 'final-form';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Field, Form, FormSpy } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { useCookie } from 'react-use';
import styled from 'styled-components';

import { getCountriesForFeature } from 'apis/merchant-onboarding-service/api';
import { APIErrorNotification, RFFFieldErrorWrapper } from 'common/components';
import { useHCaptcha } from 'common/hooks';
import { HCaptchaErrorStatus, isHCaptchaErrorStatus } from 'common/hooks/useHCaptcha';
import { Typography as T } from 'common/typography';
import validateYupSchema from 'common/utils/validateYupSchema';
import { merchantSelfServiceSignUpDataFormSubmissionStatus } from 'core/telemetry/avo/Avo';
import { useRegisterTelemetryEvent } from 'core/telemetry/hooks/useRegisterTelemetryEvent';
import { getLanguage } from 'locale';

import VenueDetails from './VenueDetails';
import { postInboundMerchant } from './api';
import { ALL_COUNTRIES, COUNTRY_CODE_EXTENSIONS } from './countryCodes';
import { getCountry } from './countryData';
import { formValuesToApi } from './transformers';
import {
  PhoneNumber,
  RedirectType,
  SelfServeSignupRequest,
  SelfServeSignupTag,
  SelfServeSignupTagName,
  SelfServeSignupValues,
} from './types';
import { selfServeSignupValidationSchema } from './validation';
import { buildPrivacyTermsUrl, buildUserTermsUrl } from '../../../../locale/utils';
import { useCookieConsents } from '../../../cookie-consents/CookieConsentsProvider';

type Props = { onSuccess: (url: RedirectType) => void };

const FormWrapper = styled.form`
  max-width: 100%;
`;

const FormContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ContactInfo = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 10px;
  row-gap: 16px;
`;

const InputExtend = styled.div`
  flex: 1 1 auto;
`;

const NoticeContainer = styled(T.Body3)`
  color: ${cbSemanticCssVars.textSecondary};
`;

const StyledLink = styled.a`
  font-weight: bold;
`;

const ErrorAlert = styled(Alert).attrs({
  variant: 'error',
})`
  margin-block-start: 16px;
`;

const getHCaptchaErrorMessage = (status: HCaptchaErrorStatus): JSX.Element => {
  switch (status) {
    case 'error':
      return <FormattedMessage id="landingPage.reCaptchaError" />;
    case 'expired':
      return <FormattedMessage id="landingPage.reCaptchaExpired" />;
    case 'timeout':
      return <FormattedMessage id="landingPage.hCaptchaTimedOut" />;
  }
};

export const getTagsFromSearchParams = (searchParams: URLSearchParams) =>
  Object.values(SelfServeSignupTagName)
    .map(tag => tag.toString().toLowerCase())
    .filter(tagName => searchParams.has(tagName))
    .map(tagName => ({
      name: tagName,
      value: searchParams.get(tagName),
    })) as SelfServeSignupTag[];

const SelfServeSignUpForm = ({ onSuccess }: Props) => {
  const { sitekey, captchaRef, hCaptchaStatus, runWithHCaptcha, hCaptchaHandlers } = useHCaptcha('mos');
  const intl = useIntl();
  const t = useCallback((id: string) => intl.formatMessage({ id }), [intl]);
  const { data: enabledCountries = [] } = useQuery(
    ['/merchant-admin/config/features', 'self-serve-sign-up'],
    () => getCountriesForFeature('self-serve-sign-up'),
    {
      refetchOnWindowFocus: false,
    },
  );

  const {
    consents: { analytics: analyticsConsent, marketing: marketingConsent },
  } = useCookieConsents();

  const register = useRegisterTelemetryEvent();
  const { search } = useLocation();
  const [woltUid] = useCookie(getCookieName('wolt-uid'));
  const searchParams = new URLSearchParams(search);

  const countryParam = (searchParams.get('country') || '').toUpperCase();
  const countryForParam = getCountry(countryParam)?.code || null;
  const [country, setCountry] = useState<string | null>(countryForParam);
  const [hasInteractedWithForm, setHasInteractedWithForm] = useState(false);

  const { key: selectedLanguage } = getLanguage();

  useEffect(() => {
    if (hasInteractedWithForm) {
      register({
        event: 'form_interaction',
        marketingConsent,
        analyticsConsent,
        properties: {
          __woltUid: woltUid,
          country,
        },
      });
    }
  }, [hasInteractedWithForm, country, register, woltUid, marketingConsent, analyticsConsent]);

  const PRODUCT_LINES = {
    restaurant: t('self-serve-sign-up.form-labels.product-lines.restaurant'),
    alcohol: t('self-serve-sign-up.form-labels.product-lines.alcohol'),
    charity: t('self-serve-sign-up.form-labels.product-lines.charity'),
    florist: t('self-serve-sign-up.form-labels.product-lines.florist'),
    general_merchandise: t('self-serve-sign-up.form-labels.product-lines.general_merchandise'),
    grocery: t('self-serve-sign-up.form-labels.product-lines.grocery'),
    health_and_beauty: t('self-serve-sign-up.form-labels.product-lines.health_and_beauty'),
    pet_supply: t('self-serve-sign-up.form-labels.product-lines.pet_supply'),
    pharmacy: t('self-serve-sign-up.form-labels.product-lines.pharmacy'),
    other: t('self-serve-sign-up.form-labels.product-lines.other_line'),
  };

  const countryOptions = ALL_COUNTRIES.filter(country => enabledCountries.includes(country.id)).map(country => ({
    value: country.id,
    label: country.value,
  }));

  const businessTypeOptions = Object.entries(PRODUCT_LINES).map(([key, value]) => ({
    value: key,
    label: value,
  }));

  const initialValues: SelfServeSignupValues = useMemo(
    () => ({
      language: selectedLanguage || '',
      country: country || '',
      businessType: '',
      firstName: '',
      lastName: '',
      name: '',
      phoneNumber: { extension: '', number: '' },
      email: '',
      venues: {
        name: '',
        address: '',
        postCode: '',
        city: '',
        isWoltCity: true,
        otherCity: '',
        venueNumber: undefined,
      },
      businessId: '',
    }),
    [selectedLanguage, country],
  );

  const postInboundMerchantWithHCaptcha = (formValues: SelfServeSignupRequest) =>
    runWithHCaptcha<{ redirect: RedirectType }>(async hCaptchaResponse => {
      return await postInboundMerchant(formValues, hCaptchaResponse);
    });

  const handleSubmit = async (origValues: SelfServeSignupValues): Promise<{ [key: string]: string | Error } | void> => {
    const values: SelfServeSignupValues = {
      ...origValues,
      // see: https://final-form.org/docs/final-form/faq#why-does-final-form-set-my--field-value-to-undefined
      // this should be handled somewhere in yup probably but as a fix for the launch we went with this. sorry :/
      phoneNumber: {
        extension: origValues.phoneNumber?.extension || '',
        number: origValues.phoneNumber?.number || '',
      },
    };

    try {
      const validFormValues = selfServeSignupValidationSchema.validateSync(values);
      const transformedValues = formValuesToApi(validFormValues);

      const requestPayload: SelfServeSignupRequest = {
        ...transformedValues,
        tags: getTagsFromSearchParams(searchParams),
        woltUid,
      };

      const response = await postInboundMerchantWithHCaptcha(requestPayload);

      const redirectType = response.redirect;

      merchantSelfServiceSignUpDataFormSubmissionStatus({
        formSubmissionStatus: 'success',
        businessId: values.businessId,
      });

      const { country, email, venues } = requestPayload;

      register({
        event: 'form_sent',
        marketingConsent,
        analyticsConsent,
        properties: {
          __woltUid: woltUid,
          city: venues[0].city,
          country,
          email,
        },
      });

      onSuccess(redirectType);
      return;
    } catch (submitError) {
      merchantSelfServiceSignUpDataFormSubmissionStatus({
        formSubmissionStatus: 'error',
        businessId: values.businessId,
      });

      return { [FORM_ERROR]: submitError as Error };
    }
  };

  const woltPrivacyStatementContent = useMemo(
    () => (
      <StyledLink href={buildPrivacyTermsUrl(selectedLanguage, country || undefined)} target="_blank" rel="noopener">
        <FormattedMessage id="self-serve-sign-up.agreement.terms-of-service.wolt-privacy-statement" />
      </StyledLink>
    ),
    [country, selectedLanguage],
  );

  const woltTermsStatementContent = useMemo(
    () => (
      <StyledLink href={buildUserTermsUrl(selectedLanguage, country || undefined)} target="_blank" rel="noopener">
        <FormattedMessage id="self-serve-sign-up.agreement.terms-of-service.wolt-terms-statement" />
      </StyledLink>
    ),
    [country, selectedLanguage],
  );

  return (
    <>
      <Form<SelfServeSignupValues>
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validate={validateYupSchema(selfServeSignupValidationSchema)}
      >
        {({ submitting, handleSubmit, submitError, hasValidationErrors, submitFailed }) => (
          <>
            <FormSpy
              subscription={{ modified: true }}
              onChange={(props): void => {
                if (hasInteractedWithForm) {
                  return;
                }

                // props.modified is an object with the keys of the form fields
                // and the values are booleans indicating if the field has been modified from their initial value
                const modifiedFormFieldValues = Object.values(props.modified || {});

                // if any of the form fields have been modified then we know the user has interacted with the form
                if (modifiedFormFieldValues.some(value => value)) {
                  setHasInteractedWithForm(true);
                }
              }}
            />
            <FormWrapper onSubmit={handleSubmit}>
              <FormContentWrapper>
                <T.Heading5>
                  <FormattedMessage id="self-serve-sign-up.subheading-text.get-started" />
                </T.Heading5>

                {/*  <div>
                  <FormattedMessage
                    id="self-serve-sign-up.subheading-text.already-have-account"
                    values={{
                      loginLink: (
                        <StyledLink href="/login" target="_blank" rel="noopener">
                          <FormattedMessage id="self-serve-sign-up.subheading-text.login" />
                        </StyledLink>
                      ),
                    }}
                  />
                </div> */}

                <Field<string | undefined> name="country" data-test-id="self-serve-sign-up-country">
                  {field => (
                    <RFFFieldErrorWrapper field={field}>
                      {input => (
                        <Select
                          placeholder={t('self-serve-sign-up.form-labels.country')}
                          inputId="country"
                          options={countryOptions}
                          {...input}
                          value={country}
                          onChange={newValue => {
                            input.onChange(newValue);
                            setCountry(newValue);
                          }}
                        />
                      )}
                    </RFFFieldErrorWrapper>
                  )}
                </Field>
                <Field<string | undefined> name="name">
                  {field => (
                    <RFFFieldErrorWrapper field={field}>
                      {input => (
                        <Input
                          {...input}
                          id="name"
                          placeholder={t('self-serve-sign-up.form-labels.name.text')}
                          disabled={submitting}
                        />
                      )}
                    </RFFFieldErrorWrapper>
                  )}
                </Field>
                <ContactInfo>
                  <Field<string | undefined> name="businessId">
                    {field => (
                      <RFFFieldErrorWrapper field={field}>
                        {input => (
                          <Input
                            {...input}
                            placeholder={t('self-serve-sign-up.form-labels.businessId')}
                            disabled={submitting}
                          />
                        )}
                      </RFFFieldErrorWrapper>
                    )}
                  </Field>

                  <Field<string | undefined> name="businessType">
                    {field => (
                      <RFFFieldErrorWrapper field={field}>
                        {input => (
                          <Select
                            placeholder={t('self-serve-sign-up.form-labels.business-type')}
                            data-test-id="self-serve-sign-up-business-type"
                            options={businessTypeOptions}
                            {...input}
                          />
                        )}
                      </RFFFieldErrorWrapper>
                    )}
                  </Field>
                </ContactInfo>

                <VenueDetails country={country} />

                <ContactInfo>
                  <Field<string | undefined> name="firstName">
                    {field => (
                      <RFFFieldErrorWrapper field={field}>
                        {input => <Input {...input} placeholder={t('self-serve-sign-up.form-labels.first-name')} />}
                      </RFFFieldErrorWrapper>
                    )}
                  </Field>

                  <Field<string | undefined> name="lastName">
                    {field => (
                      <RFFFieldErrorWrapper field={field}>
                        {input => <Input {...input} placeholder={t('self-serve-sign-up.form-labels.last-name')} />}
                      </RFFFieldErrorWrapper>
                    )}
                  </Field>

                  <InputExtend data-test-id="self-serve-sign-up-phoneNumber-extension">
                    <Field<PhoneNumber['extension']> name="phoneNumber.extension">
                      {field => (
                        <InputContainer>
                          <RFFFieldErrorWrapper field={field}>
                            {input => (
                              <Select
                                placeholder=""
                                inputId="phoneNumber.extension"
                                options={COUNTRY_CODE_EXTENSIONS.map(country => ({
                                  value: country.id,
                                  label: country.value,
                                }))}
                                menuPlacement="top"
                                {...input}
                              />
                            )}
                          </RFFFieldErrorWrapper>
                        </InputContainer>
                      )}
                    </Field>
                  </InputExtend>
                  <InputExtend data-test-id="self-serve-sign-up-phoneNumber-number">
                    <Field<PhoneNumber['number']> name="phoneNumber.number">
                      {field => (
                        <InputContainer>
                          <RFFFieldErrorWrapper field={field}>
                            {input => (
                              <Input
                                {...input}
                                id="phoneNumber.number"
                                value={input.value || ''}
                                placeholder={t('self-serve-sign-up.form-labels.phone-number')}
                                disabled={submitting}
                              />
                            )}
                          </RFFFieldErrorWrapper>
                        </InputContainer>
                      )}
                    </Field>
                  </InputExtend>
                </ContactInfo>
                <InputExtend>
                  <Field<string | undefined> name="email">
                    {field => (
                      <RFFFieldErrorWrapper field={field}>
                        {input => (
                          <Input
                            {...input}
                            id="email"
                            placeholder={t('self-serve-sign-up.form-labels.email')}
                            disabled={submitting}
                          />
                        )}
                      </RFFFieldErrorWrapper>
                    )}
                  </Field>
                </InputExtend>
                <NoticeContainer>
                  <FormattedMessage
                    id="self-serve-sign-up.agreement.terms-of-service.description"
                    values={{
                      woltPrivacyStatementUrl: woltPrivacyStatementContent,
                      woltTermsStatementUrl: woltTermsStatementContent,
                    }}
                  />
                </NoticeContainer>

                {submitError && <APIErrorNotification error={submitError} />}
                {hasValidationErrors && submitFailed && (
                  <Alert size="small" variant="error">
                    {t('self-serve-sign-up.validation-error')}
                  </Alert>
                )}
                {isHCaptchaErrorStatus(hCaptchaStatus) && (
                  <ErrorAlert>{getHCaptchaErrorMessage(hCaptchaStatus)}</ErrorAlert>
                )}
                <Button type="submit" size="large" stretch variant="blue" loading={submitting} disabled={submitting}>
                  <FormattedMessage id="self-serve-sign-up.submit" />
                </Button>
              </FormContentWrapper>
            </FormWrapper>
          </>
        )}
      </Form>
      <HCaptcha size="invisible" sitekey={sitekey} ref={captchaRef} {...hCaptchaHandlers} />
    </>
  );
};

export default SelfServeSignUpForm;
